import type { TweetMedia as ParsedTweetMedia } from '@/lib/tweet-parser';
import type { ProcessedMediaItem } from '@/lib/twitter/media-utils';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';
import type { MediaItem } from '@/types/tweet';
import type { MediaMetadataEntry } from '@/types/tweet/media';
import type { UIMediaItem, UIMediaVariant } from '@/types/ui-media';

type CanonicalMediaType = 'photo' | 'video' | 'animated_gif';
type MediaTypeCandidate = string | null | undefined;
type UrlLike = string | null | undefined;
type NumericLike = number | null | undefined;

const VIDEO_HOST_HINTS = ['video.twimg.com', 'ton.twimg.com'];
const VIDEO_SUFFIX_HINTS = ['.mp4', '.m3u8'];
const VIDEO_PATH_HINTS = ['/ext_tw_video/', '/tweet_video/', '/amplify_video/', '/video/'];

function normalizeMediaTypeCandidate(type: MediaTypeCandidate): CanonicalMediaType | undefined {
  if (typeof type !== 'string') {
    return;
  }
  const lower = type.toLowerCase();
  if (lower === 'photo' || lower === 'video' || lower === 'animated_gif') {
    return lower;
  }
  if (lower.includes('video')) {
    return 'video';
  }
  if (lower.includes('gif')) {
    return 'animated_gif';
  }
  if (lower.includes('image') || lower.includes('photo')) {
    return 'photo';
  }
  return;
}

// Detect URLs that Twitter delivers for inline clips so we can recover the correct media type
function isLikelyVideoUrl(url: UrlLike): boolean {
  if (typeof url !== 'string' || url.trim().length === 0) {
    return false;
  }
  const trimmed = url.trim();
  const lower = trimmed.toLowerCase();

  if (lower.startsWith('blob:') || lower.startsWith('data:video/')) {
    return true;
  }

  if (VIDEO_SUFFIX_HINTS.some((suffix) => lower.includes(suffix))) {
    return true;
  }

  try {
    const parsed = new URL(trimmed);
    const hostname = parsed.hostname.toLowerCase();
    if (VIDEO_HOST_HINTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))) {
      return true;
    }
    const pathname = parsed.pathname.toLowerCase();
    if (VIDEO_SUFFIX_HINTS.some((suffix) => pathname.endsWith(suffix))) {
      return true;
    }
    if (VIDEO_PATH_HINTS.some((hint) => pathname.includes(hint))) {
      return true;
    }
  } catch {
    if (VIDEO_PATH_HINTS.some((hint) => lower.includes(hint))) {
      return true;
    }
  }

  return false;
}

function hasPlayableVideoVariant(variants: MediaItem['variants']): boolean {
  if (!Array.isArray(variants)) {
    return false;
  }
  return variants.some((variant) => isLikelyVideoUrl(variant?.url ?? null));
}

// Twitter occasionally tags inline videos as "photo" in the REST payload. Normalize them here so the UI
// renders the proper player instead of the photo component.
function resolveMediaType(item: MediaItem, meta?: MediaMetadataEntry): CanonicalMediaType {
  const candidates: Array<CanonicalMediaType | undefined> = [
    normalizeMediaTypeCandidate(item.type),
    meta?.type ? normalizeMediaTypeCandidate(meta.type) : undefined,
  ];

  for (const candidate of candidates) {
    if (candidate === 'video' || candidate === 'animated_gif') {
      return candidate;
    }
  }

  const looksLikeVideo =
    hasPlayableVideoVariant(item.variants) ||
    isLikelyVideoUrl(item.url) ||
    isLikelyVideoUrl(meta?.variantUrl ?? null) ||
    (typeof meta?.durationMs === 'number' && (meta.durationMs ?? 0) > 0) ||
    (typeof item.durationMs === 'number' && (item.durationMs ?? 0) > 0);

  if (looksLikeVideo) {
    return 'video';
  }

  return candidates.find((candidate) => candidate === 'photo') ?? 'photo';
}

function sanitizeUrlCandidate(candidate: UrlLike): string | undefined {
  if (typeof candidate !== 'string') {
    return;
  }
  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeMediaMetadata(metadata: MediaMetadataEntry[] | null | undefined): Map<string, MediaMetadataEntry> {
  if (!Array.isArray(metadata)) {
    return new Map();
  }

  const map = new Map<string, MediaMetadataEntry>();
  for (const entry of metadata) {
    if (entry && typeof entry.key === 'string') {
      map.set(entry.key, entry);
    }
  }
  return map;
}

function shouldHideMedia(meta?: MediaMetadataEntry): boolean {
  return Boolean(meta?.dedupe?.hidden);
}

function sanitizeVariants(variants: MediaItem['variants']): UIMediaVariant[] | undefined {
  if (!Array.isArray(variants)) {
    return;
  }

  const normalized: UIMediaVariant[] = [];

  for (const variant of variants) {
    if (!variant) {
      continue;
    }
    const url = sanitizeUrlCandidate(variant.url);
    if (!url) {
      continue;
    }
    if (typeof variant.contentType !== 'string' || variant.contentType.length === 0) {
      continue;
    }

    const uiVariant: UIMediaVariant = {
      url,
      contentType: variant.contentType,
    };
    if (typeof variant.bitRate === 'number') {
      uiVariant.bitRate = variant.bitRate;
    }
    normalized.push(uiVariant);
  }

  return normalized.length > 0 ? normalized : undefined;
}

function mergeVariantsWithDefault(
  variants: UIMediaVariant[] | undefined,
  defaultVariant: UIMediaVariant,
  preferredBitrate?: number
): UIMediaVariant[] {
  if (!variants || variants.length === 0) {
    return [{ ...defaultVariant }];
  }

  const hasDefault = variants.some((variant) => variant.url === defaultVariant.url);
  if (!hasDefault) {
    return [...variants, defaultVariant];
  }

  if (typeof preferredBitrate === 'number') {
    return variants.map((variant) =>
      variant.url === defaultVariant.url && (variant.bitRate === undefined || Number.isNaN(variant.bitRate))
        ? { ...variant, bitRate: preferredBitrate }
        : variant
    );
  }

  return variants;
}

function resolveMediaUrlAndVariants(
  item: MediaItem,
  meta: MediaMetadataEntry | undefined,
  type: CanonicalMediaType
): { url?: string | undefined; variants?: UIMediaVariant[] | undefined } {
  let url = sanitizeUrlCandidate(item.url ?? undefined);
  let variants = sanitizeVariants(item.variants ?? undefined);

  if (!url && (type === 'video' || type === 'animated_gif')) {
    const variantUrl = sanitizeUrlCandidate(meta?.variantUrl ?? undefined);
    if (variantUrl) {
      url = variantUrl;
      const defaultVariant: UIMediaVariant = {
        url: variantUrl,
        contentType: 'video/mp4',
      };
      if (typeof meta?.variantBitrate === 'number') {
        defaultVariant.bitRate = meta.variantBitrate;
      }
      variants = mergeVariantsWithDefault(variants, defaultVariant, meta?.variantBitrate ?? undefined);
    }
  }

  return { url, variants };
}

function resolvePreviewImageUrl(item: MediaItem, meta: MediaMetadataEntry | undefined): string | undefined {
  return (
    sanitizeUrlCandidate(item.previewImageUrl ?? undefined) ?? sanitizeUrlCandidate(meta?.previewImageUrl ?? undefined)
  );
}

function pickNumeric(primary: NumericLike, fallback: NumericLike): number | undefined {
  if (typeof primary === 'number' && Number.isFinite(primary)) {
    return primary;
  }
  if (typeof fallback === 'number' && Number.isFinite(fallback)) {
    return fallback;
  }
  return;
}

export function transformProcessedMediaToUI(
  media: MediaItem[] | undefined,
  mediaMetadata?: MediaMetadataEntry[] | null
): UIMediaItem[] {
  if (!Array.isArray(media) || media.length === 0) {
    return [];
  }

  const metaByKey = normalizeMediaMetadata(mediaMetadata);

  const items: UIMediaItem[] = [];
  for (const item of media) {
    const meta = item.mediaKey ? metaByKey.get(item.mediaKey) : undefined;
    if (shouldHideMedia(meta)) {
      continue;
    }

    const type = resolveMediaType(item, meta);
    const { url, variants } = resolveMediaUrlAndVariants(item, meta, type);
    if (!url) {
      continue;
    }
    const previewImageUrl = resolvePreviewImageUrl(item, meta);

    const uiItem: UIMediaItem = {
      mediaKey: item.mediaKey,
      type,
      url,
    };
    if (previewImageUrl) {
      uiItem.previewImageUrl = previewImageUrl;
    }
    const width = pickNumeric(item.width, meta?.width);
    if (width !== undefined) {
      uiItem.width = width;
    }
    const height = pickNumeric(item.height, meta?.height);
    if (height !== undefined) {
      uiItem.height = height;
    }
    const altText = item.altText ?? meta?.altText ?? undefined;
    if (altText) {
      uiItem.altText = altText;
    }
    const durationMs = pickNumeric(item.durationMs, meta?.durationMs);
    if (durationMs !== undefined) {
      uiItem.durationMs = durationMs;
    }
    if (variants && variants.length > 0) {
      uiItem.variants = variants;
    }
    items.push(uiItem);
  }
  return items;
}

export function processedMediaToMediaItems(media: ProcessedMediaItem[]): MediaItem[] {
  const items: MediaItem[] = [];
  for (const item of media) {
    if (typeof item.media_key !== 'string' || typeof item.url !== 'string') {
      continue;
    }
    const normalizedType = normalizeMediaTypeCandidate(item.type);
    if (!normalizedType) {
      continue;
    }
    const normalized: MediaItem = {
      mediaKey: item.media_key,
      type: normalizedType,
      url: item.url,
    };
    if (item.preview_image_url) {
      normalized.previewImageUrl = item.preview_image_url;
    }
    if (typeof item.width === 'number') {
      normalized.width = item.width;
    }
    if (typeof item.height === 'number') {
      normalized.height = item.height;
    }
    if (typeof item.alt_text === 'string' && item.alt_text.length > 0) {
      normalized.altText = item.alt_text;
    }
    if (typeof item.duration_ms === 'number') {
      normalized.durationMs = item.duration_ms;
    }
    if (Array.isArray(item.variants)) {
      const variants = item.variants
        .map((variant) => {
          if (typeof variant.url !== 'string' || typeof variant.content_type !== 'string') {
            return null;
          }
          const normalizedVariant: UIMediaVariant = {
            url: variant.url,
            contentType: variant.content_type,
          };
          if (typeof variant.bit_rate === 'number') {
            normalizedVariant.bitRate = variant.bit_rate;
          }
          return normalizedVariant;
        })
        .filter((variant): variant is UIMediaVariant => variant !== null);
      if (variants.length > 0) {
        normalized.variants = variants;
      }
    }
    items.push(normalized);
  }
  return items;
}

export function normalizeTweetMediaItems(media: unknown): ParsedTweetMedia[] | undefined {
  if (!Array.isArray(media)) {
    return;
  }

  const normalized: ParsedTweetMedia[] = [];

  for (const candidate of media) {
    if (!isRecord(candidate)) {
      continue;
    }

    const mediaKey = (() => {
      const camelCaseKey = getRecordValue(candidate, 'mediaKey');
      const direct = typeof camelCaseKey === 'string' ? camelCaseKey : undefined;
      if (direct) return direct;
      const snakeCaseKeyCandidate = getRecordValue(candidate, 'media_key');
      const snakeCaseKey = typeof snakeCaseKeyCandidate === 'string' ? snakeCaseKeyCandidate : undefined;
      return snakeCaseKey;
    })();

    if (!mediaKey) {
      continue;
    }

    const typeCandidate = getRecordValue(candidate, 'type');
    const normalizedType = normalizeMediaTypeCandidate(typeof typeCandidate === 'string' ? typeCandidate : undefined);
    if (!normalizedType) {
      continue;
    }
    const urlValue = getRecordValue(candidate, 'url');
    const url = typeof urlValue === 'string' ? urlValue : undefined;
    const widthValue = getRecordValue(candidate, 'width');
    const width = typeof widthValue === 'number' ? widthValue : undefined;
    const heightValue = getRecordValue(candidate, 'height');
    const height = typeof heightValue === 'number' ? heightValue : undefined;

    if (!url) {
      continue;
    }

    const normalizedMedia: ParsedTweetMedia = {
      mediaKey,
      type: normalizedType,
      url,
    };
    if (width !== undefined) {
      normalizedMedia.width = width;
    }
    if (height !== undefined) {
      normalizedMedia.height = height;
    }
    normalized.push(normalizedMedia);
  }

  return normalized.length > 0 ? normalized : undefined;
}
